import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import MarketsPage from './pages/MarketsPage';
import TradingPage from './pages/TradingPage';
import FuturesTradingPage from './pages/FuturesTradingPage';
import P2PPage from './pages/P2PPage';
import P2POrderPage from './pages/P2POrderPage';
import P2POrdersHistoryPage from './pages/P2POrdersHistoryPage';
import WalletPage from './pages/WalletPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminManagementPage from './pages/AdminManagementPage';
import AdminTokensPage from './pages/AdminTokensPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserDetailsPage from './pages/AdminUserDetailsPage';
import AdminKYCPage from './pages/AdminKYCPage';
import AdminWalletSettings from './pages/AdminWalletSettings';
import AdminWalletKeys from './pages/AdminWalletKeys';
import AdminDepositsPage from './pages/AdminDepositsPage';
import AdminWithdrawalsPage from './pages/AdminWithdrawalsPage';
import AdminSpotTradesPage from './pages/AdminSpotTradesPage';
import AdminFuturesTradesPage from './pages/AdminFuturesTradesPage';
import AdminP2PTradesPage from './pages/AdminP2PTradesPage';
import AdminP2PAdsPage from './pages/AdminP2PAdsPage';
import AdminAnnouncementsPage from './pages/AdminAnnouncementsPage';
import AdminSupportPage from './pages/AdminSupportPage';
import AdminFeeSettingsPage from './pages/AdminFeeSettingsPage';
import AdminReportsPage from './pages/AdminReportsPage';
import AdminSecurityPage from './pages/AdminSecurityPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminReferralSettingsPage from './pages/AdminReferralSettingsPage';
import AdminTokenLaunchPage from './pages/AdminTokenLaunchPage';
import AdminBalanceManagementPage from './pages/AdminBalanceManagementPage';
import AdminBinanceSettingsPage from './pages/AdminBinanceSettingsPage';
import AdminBotTradingPage from './pages/AdminBotTradingPage';
import AdminFuturesBotPage from './pages/AdminFuturesBotPage';
import AdminProfilePage from './pages/AdminProfilePage';
import SupportPage from './pages/SupportPage';
import ReferralPage from './pages/ReferralPage';
import ProfilePage from './pages/ProfilePage';
import BotTradingPage from './pages/BotTradingPage';
import FuturesBotTradingPage from './pages/FuturesBotTradingPage';
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import RiskDisclosure from './pages/legal/RiskDisclosure';
import FeeSchedule from './pages/legal/FeeSchedule';
import FAQPage from './pages/support/FAQPage';
import AnnouncementsPage from './pages/support/AnnouncementsPage';
import DepositPage from './pages/financial/DepositPage';
import WithdrawPage from './pages/financial/WithdrawPage';
import TransactionHistoryPage from './pages/financial/TransactionHistoryPage';
import TwoFactorSetupPage from './pages/security/TwoFactorSetupPage';
import ConvertPage from './pages/ConvertPage';
import StakingPage from './pages/StakingPage';
import LaunchpadPage from './pages/LaunchpadPage';
import NotFound from './pages/NotFound';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <div className="App">
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#1e293b',
              color: '#e2e8f0',
              border: '1px solid #334155',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />

        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/risk" element={<RiskDisclosure />} />
          <Route path="/fees" element={<FeeSchedule />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/announcements" element={<AnnouncementsPage />} />

          {/* Protected Routes */}
          <Route path="/deposit" element={<ProtectedRoute><DepositPage /></ProtectedRoute>} />
          <Route path="/withdraw" element={<ProtectedRoute><WithdrawPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><TransactionHistoryPage /></ProtectedRoute>} />
          <Route path="/security/2fa" element={<ProtectedRoute><TwoFactorSetupPage /></ProtectedRoute>} />

          <Route path="/convert" element={<ProtectedRoute><ConvertPage /></ProtectedRoute>} />
          <Route path="/staking" element={<StakingPage />} />
          <Route path="/launchpad" element={<LaunchpadPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/markets"
            element={
              <ProtectedRoute>
                <MarketsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trade/:symbol?"
            element={
              <ProtectedRoute>
                <TradingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/futures/:symbol?"
            element={
              <ProtectedRoute>
                <FuturesTradingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <WalletPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bot-trading"
            element={
              <ProtectedRoute>
                <BotTradingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/futures-bot"
            element={
              <ProtectedRoute>
                <FuturesBotTradingPage />
              </ProtectedRoute>
            }
          />

          {/* P2P Routes */}
          <Route
            path="/p2p"
            element={
              <ProtectedRoute>
                <P2PPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p/orders"
            element={
              <ProtectedRoute>
                <P2POrdersHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p/orders/:id"
            element={
              <ProtectedRoute>
                <P2POrderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <ProtectedRoute>
                <ReferralPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/profile"
            element={
              <ProtectedRoute adminOnly>
                <AdminProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manage-admins"
            element={
              <ProtectedRoute adminOnly>
                <AdminManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tokens"
            element={
              <ProtectedRoute adminOnly>
                <AdminTokensPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/token-launch"
            element={
              <ProtectedRoute adminOnly>
                <AdminTokenLaunchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute adminOnly>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/kyc-requests"
            element={
              <ProtectedRoute adminOnly>
                <AdminKYCPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <ProtectedRoute adminOnly>
                <AdminUserDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/wallet-settings"
            element={
              <ProtectedRoute adminOnly>
                <AdminWalletSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/wallet-keys"
            element={
              <ProtectedRoute adminOnly>
                <AdminWalletKeys />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/deposits"
            element={
              <ProtectedRoute adminOnly>
                <AdminDepositsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/withdrawals"
            element={
              <ProtectedRoute adminOnly>
                <AdminWithdrawalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trades/spot"
            element={
              <ProtectedRoute adminOnly>
                <AdminSpotTradesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trades/futures"
            element={
              <ProtectedRoute adminOnly>
                <AdminFuturesTradesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trades/p2p"
            element={
              <ProtectedRoute adminOnly>
                <AdminP2PTradesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/p2p-ads"
            element={
              <ProtectedRoute adminOnly>
                <AdminP2PAdsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bot-trading"
            element={
              <ProtectedRoute adminOnly>
                <AdminBotTradingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/futures-bot"
            element={
              <ProtectedRoute adminOnly>
                <AdminFuturesBotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/announcements"
            element={
              <ProtectedRoute adminOnly>
                <AdminAnnouncementsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute adminOnly>
                <AdminSupportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute adminOnly>
                <AdminReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute adminOnly>
                <AdminSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/fees"
            element={
              <ProtectedRoute adminOnly>
                <AdminFeeSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/security"
            element={
              <ProtectedRoute adminOnly>
                <AdminSecurityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/referrals"
            element={
              <ProtectedRoute adminOnly>
                <AdminReferralSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/balance-management"
            element={
              <ProtectedRoute adminOnly>
                <AdminBalanceManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/binance-settings"
            element={
              <ProtectedRoute adminOnly>
                <AdminBinanceSettingsPage />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
